import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpDown, Briefcase, GraduationCap, Lock, MapPin, Pencil, Search, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const EDIT_FIELDS = ["tahun_lulus", "desa", "rt", "rw", "kecamatan", "kabupaten", "provinsi", "aktivitas", "wa"];

export default function AlumniPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [directory, setDirectory] = useState(null);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState(1);
  const [filterTing, setFilterTing] = useState("");
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/alumni/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const loadDirectory = () => {
    if (!user) return;
    api.get("/alumni/directory").then((r) => setDirectory(r.data)).catch(() => setDirectory([]));
  };
  useEffect(loadDirectory, [user]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(-sortDir);
    else { setSortKey(key); setSortDir(1); }
  };

  const rows = useMemo(() => {
    if (!directory) return [];
    return directory
      .filter((u) => !filterTing || u.alumni_profile?.tingkatan === filterTing)
      .filter((u) => u.name.toLowerCase().includes(q.toLowerCase()))
      .sort((a, b) => {
        const va = sortKey === "name" ? a.name : a.alumni_profile?.[sortKey] || "";
        const vb = sortKey === "name" ? b.name : b.alumni_profile?.[sortKey] || "";
        return String(va).localeCompare(String(vb), "id") * sortDir;
      });
  }, [directory, q, sortKey, sortDir, filterTing]);

  const openEdit = (u) => {
    setForm({ name: u.name, email: u.email, ...(u.alumni_profile || {}) });
    setDlg(u);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/alumni/${dlg.id}`, form);
      toast.success(t("common.saved"));
      setDlg(null);
      loadDirectory();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm(t("common.confirm_delete"))) return;
    try {
      await api.delete(`/admin/alumni/${id}`);
      toast.success(t("common.deleted"));
      loadDirectory();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const SortHead = ({ id, label, className = "" }) => (
    <TableHead className={className}>
      <button onClick={() => toggleSort(id)} className={`inline-flex items-center gap-1 font-semibold hover:text-emerald-800 transition-colors ${sortKey === id ? "text-emerald-800" : ""}`} data-testid={`sort-${id}`}>
        {label} <ArrowUpDown className="h-3 w-3" />
      </button>
    </TableHead>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20" data-testid="alumni-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-emerald-950 tracking-tight">{t("alumni.title")}</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">{t("alumni.subtitle")}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          {!user && (
            <Button onClick={() => navigate("/daftar")} className="rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="alumni-register-cta">
              <UserPlus className="h-4 w-4 mr-2" /> {t("alumni.register_cta")}
            </Button>
          )}
          {user?.role === "admin" && (
            <Button asChild variant="outline" className="rounded-full border-emerald-800 text-emerald-800" data-testid="alumni-admin-list-cta">
              <Link to="/dashboard">{t("dash.alumni")} (Admin)</Link>
            </Button>
          )}
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-emerald-900 rounded-xl p-8 text-white" data-testid="alumni-total-card">
          <GraduationCap className="h-8 w-8 text-[#D4AF37] mb-3" />
          <p className="text-5xl font-bold text-[#D4AF37]">{summary?.total ?? "—"}</p>
          <p className="text-sm text-emerald-100/80 mt-1">{t("alumni.total")}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-8" data-testid="alumni-tingkatan-card">
          <h3 className="font-display text-xl font-bold text-emerald-900 mb-4">{t("alumni.by_tingkatan")}</h3>
          <div className="space-y-2.5">
            {["RA", "MI", "MTs", "MA"].map((tk) => {
              const n = summary?.by_tingkatan?.[tk] ?? 0;
              const pctVal = summary?.total ? Math.round((n / summary.total) * 100) : 0;
              return (
                <div key={tk}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-semibold text-slate-700">{tk}</span>
                    <span className="text-slate-500">{n}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-emerald-100">
                    <div className="h-1.5 rounded-full bg-emerald-700" style={{ width: `${pctVal}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-8" data-testid="alumni-provinsi-card">
          <MapPin className="h-6 w-6 text-[#D4AF37] mb-3" />
          <h3 className="font-display text-xl font-bold text-emerald-900 mb-4">{t("alumni.by_provinsi")}</h3>
          <div className="grid grid-cols-2 gap-x-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">Provinsi</p>
              <ul className="space-y-1.5 text-sm">
                {Object.entries(summary?.by_provinsi || {}).slice(0, 5).map(([prov, n]) => (
                  <li key={prov} className="flex justify-between gap-2"><span className="text-slate-700 truncate">{prov}</span><span className="font-semibold text-emerald-800">{n}</span></li>
                ))}
              </ul>
            </div>
            <div data-testid="alumni-kabupaten-list">
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">{t("alumni.by_kabupaten")}</p>
              <ul className="space-y-1.5 text-sm">
                {Object.entries(summary?.by_kabupaten || {}).slice(0, 5).map(([kab, n]) => (
                  <li key={kab} className="flex justify-between gap-2"><span className="text-slate-700 truncate">{kab}</span><span className="font-semibold text-emerald-800">{n}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-8" data-testid="alumni-aktivitas-card">
          <Briefcase className="h-6 w-6 text-[#D4AF37] mb-3" />
          <h3 className="font-display text-xl font-bold text-emerald-900 mb-4">{t("alumni.by_aktivitas")}</h3>
          <ul className="space-y-2 text-sm">
            {Object.entries(summary?.by_aktivitas || {}).slice(0, 6).map(([akt, n]) => (
              <li key={akt} className="flex justify-between gap-2"><span className="text-slate-700 truncate">{akt}</span><span className="font-semibold text-emerald-800">{n}</span></li>
            ))}
          </ul>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="lg:col-span-2 bg-white rounded-xl border border-emerald-900/10 shadow-sm p-8" data-testid="alumni-latest-card">
          <h3 className="font-display text-xl font-bold text-emerald-900 mb-5">{t("alumni.latest")}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(summary?.latest || []).map((a, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg border border-slate-100 p-3">
                <span className="h-10 w-10 rounded-full bg-emerald-800 text-white text-sm font-bold flex items-center justify-center shrink-0">
                  {a.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{a.name}</p>
                  <p className="text-xs text-slate-500">{a.tingkatan} · {a.tahun_lulus}{a.provinsi ? ` · ${a.provinsi}` : ""}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="bg-emerald-50 rounded-xl border border-emerald-900/10 p-8 flex flex-col items-start justify-center" data-testid="alumni-privacy-note">
          <Lock className="h-7 w-7 text-emerald-800 mb-3" />
          <p className="text-sm text-slate-700 leading-relaxed">{t("alumni.list_admin")}</p>
        </motion.div>
      </div>

      {/* Direktori Alumni (login) */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mt-14" data-testid="alumni-directory">
        <h2 className="font-display text-3xl font-bold text-emerald-950 mb-2">{t("alumni.directory")}</h2>
        <p className="text-sm text-slate-500 mb-6">{t("alumni.directory_note")}</p>

        {!user ? (
          <div className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-10 flex flex-col items-center text-center">
            <Lock className="h-9 w-9 text-emerald-800 mb-4" />
            <p className="text-slate-600 mb-5">{t("alumni.login_to_view")}</p>
            <Button onClick={() => navigate("/masuk")} className="rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="directory-login-cta">{t("auth.masuk")}</Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-emerald-900/10 shadow-sm overflow-hidden">
            <div className="p-4 flex flex-wrap gap-3 border-b border-slate-100">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="..." className="pl-9" data-testid="directory-search-input" />
              </div>
              <Select value={filterTing || "all"} onValueChange={(v) => setFilterTing(v === "all" ? "" : v)}>
                <SelectTrigger className="w-44" data-testid="directory-tingkatan-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("alumni.all_tingkatan")}</SelectItem>
                  {["RA", "MI", "MTs", "MA"].map((tk) => <SelectItem key={tk} value={tk}>{tk}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortHead id="name" label={t("auth.name")} />
                    <SortHead id="tingkatan" label={t("alumni.tingkatan")} />
                    <SortHead id="tahun_lulus" label={t("alumni.tahun_lulus")} />
                    <TableHead>{t("alumni.kabupaten")}</TableHead>
                    <TableHead>{t("alumni.provinsi")}</TableHead>
                    <TableHead>{t("alumni.aktivitas")}</TableHead>
                    {user.role === "admin" && <><TableHead>Email</TableHead><TableHead>WA</TableHead><TableHead></TableHead></>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((u) => (
                    <TableRow key={u.id} data-testid={`directory-row-${u.id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell>{u.alumni_profile?.tingkatan}</TableCell>
                      <TableCell>{u.alumni_profile?.tahun_lulus}</TableCell>
                      <TableCell className="text-xs">{u.alumni_profile?.kabupaten || "—"}</TableCell>
                      <TableCell className="text-xs">{u.alumni_profile?.provinsi || "—"}</TableCell>
                      <TableCell className="text-xs">{u.alumni_profile?.aktivitas || "—"}</TableCell>
                      {user.role === "admin" && (
                        <>
                          <TableCell className="text-xs">{u.email}</TableCell>
                          <TableCell className="text-xs">{u.alumni_profile?.wa || "—"}</TableCell>
                          <TableCell>
                            <div className="flex gap-1.5">
                              <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`directory-edit-${u.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button size="sm" variant="destructive" onClick={() => remove(u.id)} data-testid={`directory-delete-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">{t("common.loading")}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </motion.div>

      {/* Dialog edit (admin) */}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-emerald-950">{t("common.edit")} — {dlg?.name}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={submitEdit} className="grid grid-cols-2 gap-4 mt-2" data-testid="directory-edit-form">
              <div>
                <Label className="text-sm font-medium text-slate-700">{t("auth.name")}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" data-testid="directory-edit-name" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" data-testid="directory-edit-email" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">{t("alumni.tingkatan")}</Label>
                <Select value={form.tingkatan || ""} onValueChange={(v) => setForm({ ...form, tingkatan: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="directory-edit-tingkatan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["RA", "MI", "MTs", "MA"].map((tk) => <SelectItem key={tk} value={tk}>{tk}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {EDIT_FIELDS.map((k) => (
                <div key={k}>
                  <Label className="text-sm font-medium text-slate-700">{t(`alumni.${k}`)}</Label>
                  <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5" data-testid={`directory-edit-${k}`} />
                </div>
              ))}
              <div className="col-span-2">
                <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="directory-edit-submit">
                  {busy ? t("common.loading") : t("common.save")}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
